import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Plus, X, Save, Zap } from "lucide-react";

const CRITICALITY = ["low", "medium", "high", "critical"];

export default function AdminCustomerProfiles() {
  const [discovered, setDiscovered] = useState([]);
  const [editing, setEditing] = useState(null);   // {customer_name, tech_stack:[], sector, criticality}

  async function load() {
    try {
      const r = await api.get("/admin/customer-profiles/discovered");
      setDiscovered(r.data.customers || []);
    } catch { toast.error("Failed to load customers"); }
  }
  useEffect(() => { load(); }, []);

  function openEditor(c) {
    setEditing({
      customer_name: c.customer_name,
      tech_stack: [...(c.tech_stack || [])],
      sector: c.sector || "",
      criticality: c.criticality || "medium",
    });
  }

  async function save() {
    if (!editing) return;
    try {
      await api.put("/admin/customer-profiles/", editing);
      toast.success(`Saved profile for ${editing.customer_name}`);
      setEditing(null);
      await load();
    } catch { toast.error("Save failed"); }
  }

  return (
    <div className="min-h-screen bg-[#060606] text-white p-8" data-testid="admin-customer-profiles">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Building2 className="w-5 h-5 text-[#00F0FF]" strokeWidth={1.75} />
          <h1 className="font-display font-black text-3xl tracking-tight">CUSTOMER PROFILES</h1>
        </div>
        <p className="text-sm text-[#8E9196] max-w-2xl mb-6">
          Tell ForensiQ what tech each customer runs so the CVE ticker can highlight the fresh exploits that actually touch their environment. Tags match against CVE vendors, products and summary text.
        </p>

        <div className="border border-white/10 rounded-sm bg-[#0a0a0a]" data-testid="customers-list">
          <div className="grid grid-cols-[1fr_100px_160px_140px] px-4 py-2 border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
            <div>Customer</div>
            <div>Cases</div>
            <div>Tech stack</div>
            <div></div>
          </div>
          {discovered.length === 0 && (
            <div className="p-6 text-xs text-[#52525B] font-mono">
              No customers detected yet — open an investigation with a `customer.customer_name` first.
            </div>
          )}
          {discovered.map((c) => (
            <div key={c.customer_name}
                 className="grid grid-cols-[1fr_100px_160px_140px] items-center px-4 py-3 border-b border-white/[0.04] last:border-0 text-sm">
              <div>
                <div className="font-medium">{c.customer_name}</div>
              </div>
              <div className="text-[10px] font-mono text-[#8E9196]">{c.cases}</div>
              <div className="text-[10px] font-mono text-[#8E9196] truncate" data-testid={`stack-${c.customer_name}`}>
                {c.tech_stack?.length
                  ? c.tech_stack.slice(0, 4).join(", ") + (c.tech_stack.length > 4 ? " …" : "")
                  : <span className="text-[#FF6B35]">unmapped</span>}
              </div>
              <div className="text-right">
                <button onClick={() => openEditor(c)}
                        className="px-3 py-1.5 border border-white/10 hover:border-[#00F0FF] text-[10px] font-mono uppercase tracking-widest text-[#8E9196] hover:text-white rounded-sm"
                        data-testid={`edit-${c.customer_name}`}>
                  {c.has_profile ? "Edit" : "Add profile"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-8" onClick={() => setEditing(null)}>
          <div className="bg-[#0a0a0a] border border-[#00F0FF]/40 rounded-sm p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] mb-1">Customer Profile</div>
                <div className="font-display font-black text-xl">{editing.customer_name}</div>
              </div>
              <button onClick={() => setEditing(null)} className="text-[#8E9196] hover:text-white"><X className="w-4 h-4" /></button>
            </div>

            <div className="space-y-4 mt-4">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-1">Tech stack (comma-separated)</div>
                <input
                  value={editing.tech_stack.join(", ")}
                  onChange={(e) => setEditing({ ...editing, tech_stack: e.target.value.split(",").map(t => t.trim()).filter(Boolean) })}
                  placeholder="nginx, openssl, spring, log4j, jenkins, wordpress"
                  data-testid="tech-stack-input"
                  className="w-full bg-black border border-white/10 focus:border-[#00F0FF] rounded-sm px-3 py-2 text-sm outline-none text-white font-mono"
                />
                <div className="mt-1 text-[10px] font-mono text-[#52525B] flex items-center gap-1">
                  <Zap className="w-3 h-3" /> Lowercase product / library names work best (openssl, nginx, log4j…)
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-1">Sector</div>
                  <input
                    value={editing.sector || ""} onChange={(e) => setEditing({ ...editing, sector: e.target.value })}
                    placeholder="financial · health · retail"
                    className="w-full bg-black border border-white/10 focus:border-[#00F0FF] rounded-sm px-3 py-2 text-sm outline-none text-white"
                  />
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-1">Criticality</div>
                  <select
                    value={editing.criticality} onChange={(e) => setEditing({ ...editing, criticality: e.target.value })}
                    data-testid="criticality-select"
                    className="w-full bg-black border border-white/10 focus:border-[#00F0FF] rounded-sm px-3 py-2 text-sm outline-none text-white"
                  >
                    {CRITICALITY.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditing(null)}
                      className="px-3 py-2 border border-white/10 text-[10px] font-mono uppercase tracking-widest text-[#8E9196] hover:text-white rounded-sm">
                Cancel
              </button>
              <button onClick={save}
                      data-testid="customer-save-btn"
                      className="flex items-center gap-2 px-4 py-2 bg-[#00F0FF] text-black text-[10px] font-mono uppercase tracking-widest rounded-sm hover:bg-white">
                <Save className="w-3.5 h-3.5" /> Save profile
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
